import { Capacitor } from '@capacitor/core';

/**
 * Utility to handle safe area insets for Android devices
 * Capacitor on Android doesn't automatically provide CSS safe-area-inset variables like iOS
 * 
 * This utility works as a fallback - MainActivity.java injects the actual insets from native code,
 * but this provides a backup calculation if native injection hasn't happened yet or fails.
 * 
 * DEBUG MODE: Set window.DEBUG_SAFE_AREA_TOP and window.DEBUG_SAFE_AREA_BOTTOM to override values
 */
export const safeAreaUtils = {
  /**
   * Initialize safe area handling for Android
   * This function provides fallback calculation if native insets aren't available
   */
  init() {
    // Only run on Android
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
      return;
    }

    // DEBUG: Check for override values
    const debugTop = window.DEBUG_SAFE_AREA_TOP;
    const debugBottom = window.DEBUG_SAFE_AREA_BOTTOM;
    
    if (debugTop !== undefined || debugBottom !== undefined) {
      const root = document.documentElement;
      if (debugTop !== undefined) {
        root.style.setProperty('--safe-area-inset-top', `${debugTop}px`);
        root.setAttribute('data-safe-area-top', debugTop);
        console.log('[SafeArea] DEBUG: Override top inset to', debugTop + 'px');
      }
      if (debugBottom !== undefined) {
        root.style.setProperty('--safe-area-inset-bottom', `${debugBottom}px`);
        root.setAttribute('data-safe-area-bottom', debugBottom);
        console.log('[SafeArea] DEBUG: Override bottom inset to', debugBottom + 'px');
      }
      return; // Skip normal initialization when debug mode is active
    }

    // Check if native code has already set the safe area insets
    const checkNativeInsets = () => {
      const root = document.documentElement;
      const nativeTop = root.style.getPropertyValue('--safe-area-inset-top');
      const nativeBottom = root.style.getPropertyValue('--safe-area-inset-bottom');
      
      // If native values are set and non-zero, use them
      if (nativeTop && nativeTop !== '0px' && nativeBottom !== undefined) {
        console.log('[SafeArea] Using native insets from MainActivity:', {
          top: nativeTop,
          bottom: nativeBottom
        });
        return true;
      }
      return false;
    };

    // Function to calculate and apply safe areas (fallback only)
    const applySafeAreasFallback = () => {
      // Don't override if native values are already set
      if (checkNativeInsets()) {
        return;
      }

      const root = document.documentElement;
      
      // Get viewport dimensions
      const viewportHeight = window.innerHeight;
      const screenHeight = window.screen.height;
      
      let topInset = 0;
      let bottomInset = 0;
      
      // Method 1: Use visual viewport API if available
      if (window.visualViewport) {
        const visualTop = window.visualViewport.offsetTop || 0;
        const visualHeight = window.visualViewport.height || viewportHeight;
        const visualBottom = Math.max(0, screenHeight - (visualTop + visualHeight));
        
        topInset = Math.max(0, visualTop);
        bottomInset = Math.max(0, visualBottom);
      }
      
      // Method 2: Calculate based on screen vs viewport difference
      const heightDiff = screenHeight - viewportHeight;
      
      if (heightDiff > 0 && topInset === 0 && bottomInset === 0) {
        // Status bar is typically 24-48px, navigation bar 0-48px
        topInset = Math.min(48, Math.max(24, Math.round(heightDiff * 0.5)));
        bottomInset = Math.max(0, heightDiff - topInset);
      } else if (topInset === 0) {
        // Conservative estimate for status bar
        topInset = 24;
      }
      
      // Apply as CSS variables (only if native hasn't set them)
      if (!checkNativeInsets()) {
        root.style.setProperty('--safe-area-inset-top', `${topInset}px`);
        root.style.setProperty('--safe-area-inset-bottom', `${bottomInset}px`);
        root.style.setProperty('--safe-area-inset-left', '0px');
        root.style.setProperty('--safe-area-inset-right', '0px');
        
        root.setAttribute('data-safe-area-top', topInset);
        root.setAttribute('data-safe-area-bottom', bottomInset);
        
        console.log('[SafeArea] Fallback safe areas applied:', {
          top: topInset,
          bottom: bottomInset
        });
      }
    };

    // Wait a bit for native code to inject values first
    setTimeout(() => {
      if (!checkNativeInsets()) {
        // Native values not set, use fallback
        applySafeAreasFallback();
      }
    }, 500);

    // Re-check on orientation change and resize (native code should update, but fallback if not)
    window.addEventListener('resize', () => {
      setTimeout(() => {
        if (!checkNativeInsets()) {
          applySafeAreasFallback();
        }
      }, 100);
    });
    
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        if (!checkNativeInsets()) {
          applySafeAreasFallback();
        }
      }, 200);
    });
  }
};

